'use client';

import { faTrophy } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment-timezone';
import { useEffect, useRef, useState } from 'react';

import ChangeScoreFormatModal from '@/modules/league/new-components/change-score-format-components/ChangeFormatModal';
import EnterScoreModal from '@/modules/league/new-components/matches-components/EnterScoreComponent/EnterScoreModal';
import { ByePlayerSection } from '@/modules/league/new-components/matches-components/MatchesComponent/MatchCard/ByePlayerSection';
import { getTrophy } from '@/modules/league/new-components/matches-components/MatchesComponent/MatchCard/getTrophy';
import {
  PlayerBoxFirstTeam,
  PlayerBoxSecondTeam,
} from '@/modules/league/new-components/matches-components/MatchesComponent/MatchCard/PlayerBox';
import { ScoreSection } from '@/modules/league/new-components/matches-components/MatchesComponent/MatchCard/ScoreSection';
import SubPlayerModal from '@/modules/league/new-components/matches-components/MatchesComponent/MatchCard/SubPlayerBox';
import { GameDayInfo, MatchesPlayers, MatchesResult } from '@/modules/league/new-components/types';
import { defaultScoresArray } from '@/utils/actions/leagues/gameDay/functions/getScores';

interface MatchCardProps {
  matchesResult: MatchesResult;
  headerContent: GameDayInfo;
  isAuthorized: boolean | null;
  userUuid: string | undefined;
}
export interface Scores {
  ScoreID: string;
  Team_1_Game1Score: number;
  Team_1_Game2Score: number;
  Team_1_Game3Score: number;
  Team_1_Game4Score: number;
  Team_1_Game5Score: number;
  Team_2_Game1Score: number;
  Team_2_Game2Score: number;
  Team_2_Game3Score: number;
  Team_2_Game4Score: number;
  Team_2_Game5Score: number;
}

export const MatchCard = ({
  matchesResult,
  headerContent,
  userUuid,
  isAuthorized,
}: MatchCardProps) => {
  const { matches, matchGroups } = matchesResult;
  const { details } = headerContent;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenSubPlayer, setIsModalOpenSubPlayer] = useState(false);
  const [team1Wins, setTeam1Wins] = useState(0);
  const [team2Wins, setTeam2Wins] = useState(0);
  const [scores, setScores] = useState<{ [matchId: string]: Scores }>({});
  const [isScoreSaved, setIsScoreSaved] = useState(false);
  const [isChangeScoreFormatModalOpened, setIsChangeScoreFormatModalOpened] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<MatchesPlayers | undefined>(
    matches ? matches[0] : undefined
  );
  const [isUserAbleToEnterScore, setIsUserAbleToEnterScore] = useState<boolean | undefined>(false);
  const [selectedCourtIndex, setSelectedCourtIndex] = useState<number>(0);

  const fromDate = moment.parseZone(details.FromDateTime_UTC);
  const nowInLeagueTZ = moment().tz(details.Timezone_IanaID);
  const has24HoursPassedForTraditional =
    details.leagueTypeId === 2 && nowInLeagueTZ.isSameOrBefore(fromDate.clone().add(24, 'hours'));

  const updateSelectedPlayer = (player: MatchesPlayers) => {
    setSelectedPlayer(player);

    const byePlayerIndex = Math.floor(
      (allPlayers.indexOf(player) * byePlayers.length) / allPlayers.length
    );
    const isPlayerOnBye =
      userUuid === byePlayers[byePlayerIndex]?.T1a_UserID ||
      userUuid === byePlayers[byePlayerIndex]?.T1b_UserID ||
      userUuid === byePlayers[byePlayerIndex]?.T2a_UserID ||
      userUuid === byePlayers[byePlayerIndex]?.T2b_UserID;

    const matchingPlayer = !!(
      userUuid && matches?.find((player) => isMatchingUser(player, userUuid))
    );
    const isAbleToEnterScore =
      (matchingPlayer || isPlayerOnBye) &&
      (details.DateLockScores === null || has24HoursPassedForTraditional) &&
      details.canPlayersEnterScores;

    setIsUserAbleToEnterScore(isAbleToEnterScore);
  };

  const handleSetScores = (matchId: string, newScores: Scores) => {
    setScores((prevScores) => ({
      ...prevScores,
      [matchId]: { ...prevScores[matchId], ...newScores },
    }));
  };

  const [allPlayers, byePlayers] = matches.reduce<[MatchesPlayers[], MatchesPlayers[]]>(
    (acc, el) => {
      if (el.DisplayMatchNumber > 0 && el.DisplayMatchNumber < 999999) {
        acc[0].push(el);
      } else {
        acc[1].push(el);
      }
      return acc;
    },
    [[], []]
  );

  const isMatchingUser = (player: (typeof matches)[number], userId: string): boolean => {
    const normalizedUserId = userId.toLowerCase();
    return [player.T1a_UserID, player.T1b_UserID, player.T2a_UserID, player.T2b_UserID].some(
      (id) => id?.toLowerCase() === normalizedUserId
    );
  };

  const matchingPlayer = userUuid
    ? matches?.find((player) => isMatchingUser(player, userUuid))
    : false;

  const showSubPlayerButton =
    ((details.DateLockScores === null && isAuthorized && !details.isRatingLeague) ||
      (details.isFlexLeague && matchingPlayer)) &&
    !details.traditionalLeague;

  const matchRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

  const handleScoreUpdate = (matchIndex: number) => {
    const nextIndex = matchIndex + 1;
    setCurrentMatchIndex(nextIndex);

    if (currentMatchIndex === matchGroups.length) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      matchRefs.current[nextIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  useEffect(() => {
    if (isScoreSaved && ((matchingPlayer && details.canPlayersEnterScores) || isAuthorized)) {
      handleScoreUpdate(currentMatchIndex);
    }
  }, [matches]);

  return (
    <>
      {allPlayers.map((player, i) => {
        const byePlayerIndex = Math.floor((i * byePlayers.length) / allPlayers.length);
        const isPlayerOnBye =
          userUuid &&
          (userUuid === byePlayers[byePlayerIndex]?.T1a_UserID ||
            userUuid === byePlayers[byePlayerIndex]?.T1b_UserID ||
            userUuid === byePlayers[byePlayerIndex]?.T2a_UserID ||
            userUuid === byePlayers[byePlayerIndex]?.T2b_UserID);
        const isUserAbleToEnterScore =
          (matchingPlayer || isPlayerOnBye) &&
          (details.DateLockScores === null || has24HoursPassedForTraditional) &&
          details.canPlayersEnterScores
            ? true
            : false;
        const allowEnteringScores =
          ((isAuthorized || isUserAbleToEnterScore) &&
            (details.leagueTypeId !== 2
              ? !details.DateLockScores
              : has24HoursPassedForTraditional) &&
            details.rankingType !== 'UpDown') ||
          ((isAuthorized || isUserAbleToEnterScore) &&
            details.rankingType === 'UpDown' &&
            details.numberOfSequences > 0 &&
            details.DidGenerate2ndSequence &&
            !details.DateLockScores) ||
          ((isAuthorized || isUserAbleToEnterScore) &&
            details.rankingType === 'UpDown' &&
            !details.DidGenerate2ndSequence &&
            !details.DateLockScores);
        return (
          <div
            key={`match-${i}`}
            id={`match ${i}`}
            ref={(el: HTMLDivElement | null) => {
              matchRefs.current[i] = el;
            }}
            className={`group flex h-auto w-full flex-col gap-6 rounded-lg bg-white shadow-sm hover:bg-gray-50 md:max-w-[350px] lg:max-w-[410px] ${details.traditionalLeague ? 'min-h-80' : byePlayers[byePlayerIndex] ? 'min-h-[432px]' : 'min-h-96'}`}
          >
            {/* Match Header */}
            <div className='flex justify-between px-4 pt-4'>
              <div className='flex flex-col items-start justify-between'>
                <h3 className='flex text-base font-semibold lg:text-lg'>
                  {details.isRatingLeague ? `Court ${i + 1}` : `Match ${player.DisplayMatchNumber}`}
                </h3>
                <p
                  className={`text-sm ${!isAuthorized || details.DateLockScores ? 'text-gray-600' : 'text-leagues-500/90 cursor-pointer'}`}
                  onClick={() => {
                    if (isAuthorized && !details.DateLockScores) {
                      updateSelectedPlayer(player);
                      setSelectedCourtIndex(i);
                      setIsChangeScoreFormatModalOpened(true);
                    }
                  }}
                >
                  {!details.traditionalLeague ? player.Shorthand : player.ScoreTitle}
                </p>
              </div>
              {showSubPlayerButton ? (
                <div className='flex flex-col items-center justify-center'>
                  <button
                    className='items-center text-right text-sm font-semibold text-leagues-700 hover:cursor-pointer hover:text-leagues-500/90'
                    onClick={() => {
                      setIsModalOpenSubPlayer(true);
                      updateSelectedPlayer(player);
                    }}
                  >
                    Sub Player
                  </button>
                </div>
              ) : null}
            </div>

            {/* Players Section */}
            <div
              className={`px-4 ${allowEnteringScores ? 'hover:cursor-pointer' : 'hover:cursor-text'}`}
              onClick={() => {
                if (allowEnteringScores) {
                  setIsModalOpen(true);
                  updateSelectedPlayer(player);
                  setCurrentMatchIndex(i);
                }
              }}
            >
              <div className='flex w-full flex-row gap-2 md:gap-2 lg:gap-6'>
                <div className='flex w-16 items-center justify-center bg-white text-gray-900 group-hover:bg-gray-50 group-hover:text-green-500'>
                  {/* Trophy icon */}
                  {getTrophy(player) &&
                    player.T1a_SessionTeamID === player.SessionTeamID_Winner_a &&
                    player.T1b_SessionTeamID === player.SessionTeamID_Winner_b && (
                      <FontAwesomeIcon
                        icon={faTrophy}
                        className='flex size-6 items-center justify-center text-green-500 group-hover:opacity-100'
                      />
                    )}
                </div>
                <PlayerBoxFirstTeam
                  player={player}
                  session={details}
                  userUuid={userUuid}
                  teamNumber={1}
                />
              </div>

              {/* Score Section */}

              <ScoreSection
                player={player}
                team1Wins={team1Wins}
                team2Wins={team2Wins}
                scores={scores[player.ScoreID] || defaultScoresArray(player)}
              />

              {/* Second Set of Players */}
              <div className='flex w-full flex-row gap-2 md:gap-2 lg:gap-6'>
                <div
                  className={`flex w-16 items-center justify-center bg-white text-gray-900 group-hover:bg-gray-50`}
                >
                  {getTrophy(player) &&
                    player.T2a_SessionTeamID === player.SessionTeamID_Winner_a &&
                    player.T2b_SessionTeamID === player.SessionTeamID_Winner_b && (
                      <FontAwesomeIcon
                        icon={faTrophy}
                        className='flex size-6 items-center justify-center text-green-500 group-hover:opacity-100'
                      />
                    )}
                </div>

                <PlayerBoxSecondTeam
                  player={player}
                  session={details}
                  userUuid={userUuid}
                  teamNumber={2}
                />
              </div>
            </div>
            {/* Bye Player */}
            {byePlayers[byePlayerIndex] ? (
              <div className='flex h-full justify-between bg-gray-100 px-4'>
                <div className='flex flex-col justify-center gap-1 py-1'>
                  {/* PT1 Seed and Names */}
                  <ByePlayerSection
                    seedNumber1={byePlayers[byePlayerIndex].PT1a_PoolSeedNumber}
                    seedNumber2={byePlayers[byePlayerIndex].PT1b_PoolSeedNumber}
                    player1={{
                      firstName: byePlayers[byePlayerIndex].T1a_FirstName,
                      lastName: byePlayers[byePlayerIndex].T1a_LastName,
                      isUserAlsoPlayer:
                        userUuid?.toLowerCase() ===
                        byePlayers[byePlayerIndex].T1a_UserID?.toLowerCase(),
                    }}
                    player2={{
                      firstName: byePlayers[byePlayerIndex].T1b_FirstName,
                      lastName: byePlayers[byePlayerIndex].T1b_LastName,
                      isUserAlsoPlayer:
                        userUuid?.toLowerCase() ===
                        byePlayers[byePlayerIndex].T1b_UserID?.toLowerCase(),
                    }}
                  />
                  {/* PT2 Seed and Names */}
                  <ByePlayerSection
                    seedNumber1={byePlayers[byePlayerIndex].PT2a_PoolSeedNumber}
                    seedNumber2={byePlayers[byePlayerIndex].PT2b_PoolSeedNumber}
                    player1={{
                      firstName: byePlayers[byePlayerIndex].T2a_FirstName,
                      lastName: byePlayers[byePlayerIndex].T2a_LastName,
                      isUserAlsoPlayer:
                        userUuid?.toLowerCase() ===
                        byePlayers[byePlayerIndex].T2a_UserID?.toLowerCase(),
                    }}
                    player2={{
                      firstName: byePlayers[byePlayerIndex].T2b_FirstName,
                      lastName: byePlayers[byePlayerIndex].T2b_LastName,
                      isUserAlsoPlayer:
                        userUuid?.toLowerCase() ===
                        byePlayers[byePlayerIndex].T2b_UserID?.toLowerCase(),
                    }}
                  />
                </div>
                <div className='flex items-center text-sm font-bold text-gray-600'>BYE</div>
              </div>
            ) : null}
          </div>
        );
      })}
      {selectedPlayer ? (
        <EnterScoreModal
          openModal={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          player={selectedPlayer}
          team1Wins={team1Wins}
          team2Wins={team2Wins}
          setTeam1Wins={setTeam1Wins}
          setTeam2Wins={setTeam2Wins}
          isAuthorized={isAuthorized}
          setScores={handleSetScores}
          scores={scores[selectedPlayer.ScoreID] || defaultScoresArray(selectedPlayer)}
          isUserAbleToEnterScore={isUserAbleToEnterScore || false}
          session={details}
          userUuid={userUuid}
          setIsScoreSaved={setIsScoreSaved}
        />
      ) : null}
      {selectedPlayer ? (
        <SubPlayerModal
          openModal={isModalOpenSubPlayer}
          setIsModalOpen={setIsModalOpenSubPlayer}
          player={selectedPlayer}
          matchDateId={details.MatchDateID}
        />
      ) : null}
      {selectedPlayer && isChangeScoreFormatModalOpened ? (
        <ChangeScoreFormatModal
          openModal={isChangeScoreFormatModalOpened}
          setIsModalOpen={setIsChangeScoreFormatModalOpened}
          title={`${details.isRatingLeague ? `Court ${selectedCourtIndex + 1}` : `Match ${selectedPlayer.DisplayMatchNumber}`}`}
          currentScoreFormat={selectedPlayer.Shorthand}
          isMatchesPage
          matchDateId={details.MatchDateID}
          poolId={selectedPlayer.PoolID}
          scoreId={selectedPlayer.ScoreID}
        />
      ) : null}
    </>
  );
};
